import { bandColor, bandLabel } from '@/lib/scoreBands';

/**
 * ScoreRing — the overall score, as a full ring.
 *
 * ── AN SVG ARC WITH stroke-dasharray, NEVER A conic-gradient ────────────────
 *
 * Ruled 2026-08-17 and restated in this phase's brief, and the reason is not
 * aesthetic. The PDF path renders with Playwright and FALLS BACK TO WEASYPRINT
 * on a 60-second timeout (server.py). WeasyPrint cannot render conic-gradient,
 * so a conic ring would vanish silently — in exactly the situation nobody is
 * watching, on the artefact the customer paid for. An arc drawn with
 * stroke-dasharray is the one construction both renderers agree on.
 *
 * ── THE GEOMETRY IS THE CONTRACT ────────────────────────────────────────────
 *
 * A FULL circle, 360 degrees, starting at 12 o'clock. The spec's data slide
 * draws it that way; the web already did; **the PDF drew a 180-degree
 * semicircle gauge**, so the two have never matched. They match now, and the
 * numbers below are what makes that checkable rather than a claim: the same
 * radius, stroke and dash arithmetic is used on both sides.
 *
 *   radius        = size * 0.38
 *   stroke        = size * 0.09
 *   circumference = 2 * PI * radius
 *   filled        = circumference * score / 100
 *
 * ── COLOUR ─────────────────────────────────────────────────────────────────
 *
 * The band table, not the accent. The spec's slide draws the ring in #FF5C2B,
 * but a score has four measured bands with their own AA-checked colours and a
 * parity test against the backend; painting every score the same orange throws
 * that away and says "72" and "18" are the same thing. Documented deviation.
 */
export function ScoreRing({ score, size = 168, label = 'out of 100', animate = true }) {
  const value = Math.max(0, Math.min(100, Math.round(Number(score) || 0)));
  const radius = size * 0.38;
  const stroke = size * 0.09;
  const circumference = 2 * Math.PI * radius;
  const filled = (circumference * value) / 100;
  const color = bandColor(value);

  return (
    <div
      className="uxa-score-ring"
      style={{ position: 'relative', width: size, height: size, display: 'inline-flex' }}
      role="img"
      // The ring is a graphic, so it carries its own name rather than leaving a
      // screen reader to assemble one from two absolutely-positioned spans.
      aria-label={`Overall score ${value} ${label}, ${bandLabel(value)}`}
      data-testid="score-ring"
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="var(--track-empty)" strokeWidth={stroke}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${filled.toFixed(2)} ${(circumference - filled).toFixed(2)}`}
          // -90deg so the arc starts at 12 o'clock. A transform on the element,
          // not a CSS class, because the PDF renderer gets the same attribute.
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={animate ? { transition: 'stroke-dasharray 1.2s var(--ease-out)' } : undefined}
        />
      </svg>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 2,
        }}
      >
        <span style={{ fontSize: size * 0.28, lineHeight: 1, fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--ink-1)' }}>
          {value}
        </span>
        <span style={{ fontSize: size * 0.085, color: 'var(--ink-2)' }}>{label}</span>
      </div>
    </div>
  );
}

export default ScoreRing;
